/*
 * LE PAROLE DEL SITO NELLA LINGUA DI CHI GUARDA.
 *
 * Il sito e' scritto in italiano, dentro il codice: l'italiano e' la lingua
 * di partenza. Ogni parola che si vede passa da t('…') con la frase
 * italiana come CHIAVE, cosi' il codice resta leggibile e il dizionario ha
 * l'italiano accanto a ogni traduzione. Nelle altre lingue la parola si
 * legge dal dizionario; se manca, resta l'italiano - mai una casella vuota.
 *
 * DA DOVE ARRIVA IL DIZIONARIO: dal servizio delle skin (tenants), dove lo
 * scrive il backoffice (Sito > Lingue e parole): GET
 * /api/tenants/dizionario/<lingua>?domain=<dominio del sito>, una mappa
 * frase italiana → traduzione, con sopra le parole proprie della skin di
 * quel dominio («Schedina» che li' si chiama «Coupon»). Quali parole
 * esistono lo sa il servizio perche' le legge dal sito stesso: parole.json,
 * scritto a ogni costruzione da scripts/raccogli-parole.mjs.
 *
 * NELL'ANTEPRIMA del backoffice la bozza della skin puo' portare `parole`
 * (lingua → frase → parola) non ancora salvate: si mettono sopra il
 * dizionario appena arrivano, e di nuovo ogni volta che il dizionario si
 * ricarica. Solo in memoria: il sito vero le vede dopo «Salva».
 *
 * QUALE LINGUA: lo decide preferenze.lingua() - la scelta nella tendina,
 * altrimenti il browser, altrimenti la prima della skin - e cambia con
 * preferenze.impostaLingua(), che avvisa i18next e tutto quello che scrive
 * date e numeri.
 *
 * COSA NON FA: non traduce i nomi che arrivano dal fornitore (squadre,
 * campionati, mercati), che sono del catalogo; non impagina da destra a
 * sinistra (arabo, persiano: dopo).
 */
import i18n from 'i18next';
import {initReactI18next} from 'react-i18next';
import Backend from 'i18next-http-backend';
import {lingua, ascolta} from './preferenze';
import {ascoltaSkin, skinCorrente} from './skin';

const LINGUA_DI_PARTENZA = 'it';

/** L'indirizzo del dizionario di una lingua, sul servizio delle skin. */
function indirizzoDizionario(codice) {
  const env = (typeof window !== 'undefined' && window.env) || {};
  const base = (env.REACT_APP_API_URL || '').replace(/\/+$/, '');
  let dominio = '';
  try {
    dominio = window.location.hostname || '';
  } catch (e) { /* senza finestra (prove): nessuna skin */ }
  return base + '/api/tenants/dizionario/' + encodeURIComponent(codice)
    + (dominio ? '?domain=' + encodeURIComponent(dominio) : '');
}

/** Il browser dice al lettore di schermo in che lingua e' la pagina. */
function segnaLinguaSullaPagina(codice) {
  try {
    document.documentElement.lang = codice;
  } catch (e) { /* senza documento (prove) non c'e' niente da segnare */ }
}

i18n
    .use(Backend)
    .use(initReactI18next)
    .init({
      lng: lingua(),
      fallbackLng: LINGUA_DI_PARTENZA,
      /* Quali lingue si possono scegliere lo decide preferenze.lingua() (la
         skin); qui si carica quella che chiede. «en-GB» carica «en»: le
         lingue del dizionario sono senza paese. */
      load: 'languageOnly',

      /* La chiave E' la frase italiana: punti e due punti dentro la frase non
         sono separatori di niente. */
      keySeparator: false,
      nsSeparator: false,
      pluralSeparator: '_',
      returnNull: false,
      returnEmptyString: false,

      backend: {
        loadPath: (lingue) => indirizzoDizionario(lingue[0]),
        /* Il dizionario e' pubblico (lo chiede chi non e' ancora entrato) e
           il browser lo tiene due minuti: una parola cambiata nel backoffice
           arriva al prossimo caricamento. */
        requestOptions: {cache: 'default'},
      },

      interpolation: {
        /* React protegge gia' dal codice dentro i testi. */
        escapeValue: false,
      },

      react: {
        /* Finche' il dizionario non e' arrivato si scrive l'italiano: niente
           pagina bianca in attesa. */
        useSuspense: false,
      },

      debug: false,
    });

segnaLinguaSullaPagina(lingua());
i18n.on('languageChanged', segnaLinguaSullaPagina);

/* Le parole proprie della skin portate dalla bozza (solo in anteprima):
   sopra il dizionario, in memoria. Quando il dizionario di una lingua si
   carica dal servizio sostituisce il pacchetto intero, quindi si rimettono
   anche a ogni caricamento. */
function applicaParoleDellaBozza() {
  const s = skinCorrente();
  const parole = s && s.parole;
  if (!parole || typeof parole !== 'object') return;
  Object.keys(parole).forEach((codice) => {
    const perLingua = parole[codice];
    if (perLingua && typeof perLingua === 'object' && Object.keys(perLingua).length) {
      i18n.addResourceBundle(codice, 'translation', perLingua, true, true);
    }
  });
}
i18n.on('loaded', applicaParoleDellaBozza);

/* I PLURALI DELLE ALTRE LINGUE. Il dizionario ha due forme per ogni plurale,
   …_one e …_other, quelle dell'italiano. Il russo ne vuole quattro (one,
   few, many, other), l'arabo sei: senza la forma giusta i18next tornava la
   frase italiana. Finche' il backoffice non ha una casella per ogni forma,
   le forme che mancano usano …_other, che e' la piu' vicina. */
export function completaPlurali() {
  const negozio = (i18n.store && i18n.store.data) || {};
  Object.keys(negozio).forEach((codice) => {
    const pacchetto = negozio[codice] && negozio[codice].translation;
    if (!pacchetto) return;
    const aggiunte = {};
    Object.keys(pacchetto).forEach((chiave) => {
      if (!chiave.endsWith('_other')) return;
      const base = chiave.slice(0, -'_other'.length);
      ['_zero', '_two', '_few', '_many'].forEach((forma) => {
        if (!(base + forma in pacchetto)) aggiunte[base + forma] = pacchetto[chiave];
      });
    });
    if (Object.keys(aggiunte).length) i18n.addResources(codice, 'translation', aggiunte);
  });
}
i18n.on('loaded', completaPlurali);

/* Quando chi guarda sceglie un'altra lingua dalla tendina, e quando arriva
   la skin (con le sue lingue, la scelta di prima puo' non valere piu'). */
function allinea() {
  applicaParoleDellaBozza();
  completaPlurali();
  const voluta = lingua();
  if (voluta !== i18n.language) i18n.changeLanguage(voluta);
  else if (i18n.isInitialized) i18n.emit('languageChanged', voluta);   /* parole cambiate: si ridisegna */
}
ascolta(allinea);
ascoltaSkin(allinea);

export default i18n;
